import { WORD_TYPE, DROPPABLE_TYPE } from 'a/unmaskThat/data/constants';

export const praseOnboardingItem = (item) => {
  const wordProps = item.sentence.split(' ').map((word, i) => ({
    id: `onboarding-${i}-${word}`,
    text: word,
    type: WORD_TYPE.PLAIN,
  }));
  return {
    anaphoraIndex: item.anaphoraIndex,
    antecedentIndex: item.antecedentIndex,
    wordProps,
  };
};

export const parseStandardItem = (item, index, uuids) => {
  // Obtain min (/string/) and max ([string]) antecedents
  const minAntecedent = item.substring(
    item.indexOf('/') + 1,
    item.lastIndexOf('/'),
  );
  const maxAntecedent = item
    .substring(item.indexOf('[') + 1, item.indexOf(']'))
    .replace(/[/]/g, '');

  // The anaphoraIndex is needed in case
  // there are multiple anaphora in a sentence,
  // i.e., [Catherine] loves grapes, so she took them
  // with her wherever {she} went.
  let anaphoraIndex = -1;
  let anaphora = '';
  let antecedentIndex = -1;

  // Clean up from the antecedent, then
  // split the text into words for iteration
  // and sending to StandardItem props
  const words = item.replace(/[/\]]/g, '').split(' ');

  // Iterate to find the important indices
  for (let i = 0; i < words.length; ++i) {
    if (words[i].includes('{')) {
      anaphoraIndex = i;
      words[i] = words[i].replace(/[{}]/g, '');
      anaphora = words[i];
    } else if (words[i].includes('[')) {
      antecedentIndex = i;
      words[i] = words[i].replace(/[[]/g, '');
    }
  }

  const wordProps = words.map((word, i) => ({
    id: `${index}-${i}-${word}`,
    text: word,
    type: i === anaphoraIndex ? WORD_TYPE.ANAPHORA : WORD_TYPE.INTERACTABLE,
  }));

  return {
    anaphora,
    anaphoraIndex,
    antecedent: {
      index: antecedentIndex,
      min: minAntecedent,
      max: maxAntecedent,
    },
    sentence: words.join(' '),
    wordProps,
    uuid: uuids[index],
  };
};

export const parsePracticeItem = (item) => {
  // The anaphoraIndex is needed in case
  // there are multiple anaphora in a sentence,
  // i.e., [Catherine] loves grapes, so she took them
  // with her wherever {she} went. The same applies for
  // the antecedent and distractor
  let anaphoraIndex = -1;
  let anaphora = '';
  let antecedentIndex = -1;
  let distractorIndex = -1;

  // Split the text into words for iteration
  const words = item.split(' ');

  // Iterate to find the important indices,
  // and clean up the sentence
  for (let i = 0; i < words.length; ++i) {
    if (words[i].includes('{')) {
      anaphoraIndex = i;
      words[i] = words[i].replace(/[{}]/g, '');
      anaphora = words[i];
    } else if (words[i].includes('[')) {
      antecedentIndex = i;
      words[i] = words[i].replace(/[[\]]/g, '');
    } else if (words[i].includes('<')) {
      distractorIndex = i;
      words[i] = words[i].replace(/[<>]/g, '');
    }
  }
  const antecedentText = item.substring(item.indexOf('[') + 1, item.lastIndexOf(']'));
  const distractorText = item.substring(item.indexOf('<') + 1, item.lastIndexOf('>'));

  const wordProps = [];
  for (let i = 0; i < words.length; i++) {
    // Anaphora needs to have a highlight
    if (i === anaphoraIndex) {
      wordProps[i] = {
        id: `practice-${i}-${words[i]}`,
        text: words[i],
        type: WORD_TYPE.ANAPHORA,
      };
    } else if (i === antecedentIndex) {
      wordProps[antecedentIndex] = {
        id: `practice-${i}-${words[i]}`,
        text: antecedentText,
        type: WORD_TYPE.INTERACTABLE,
      };
      // skip to the index AFTER the length of the antecedent,
      // so that the entire antecedent will be rendered together
      // but the wordProps index won't be off
      i += antecedentText.split(' ').length - 1;
    } else if (i === distractorIndex) {
      // If the word is not an anaphora,
      // make it interactable
      wordProps[distractorIndex] = {
        id: `practice-${i}-${words[i]}`,
        text: distractorText,
        type: WORD_TYPE.INTERACTABLE,
      };
      // skip to the index AFTER the length of the distractor,
      // so that the entire distractor will be rendered together
      // but the wordProps index won't be off
      i += distractorText.split(' ').length - 1;
    } else {
      wordProps[i] = {
        id: `practice-${i}-${words[i]}`,
        text: words[i],
        type: WORD_TYPE.PLAIN,
      };
    }
  }

  return {
    anaphora,
    anaphoraIndex,
    antecedent: {
      index: antecedentIndex,
      text: antecedentText,
    },
    distractor: {
      index: distractorIndex,
      text: distractorText,
    },
    sentence: words.join(' '),
    wordProps,
  };
};

export const praseMysteryItem = (item) => {
  // Obtain anaphors' indices
  // Obtain anaphors and possible antecedents from the paragraph
  const anaphors = item.split('{');
  const antecedents = item.split('[');

  // Array for holding objects matching the anaphora to its
  // possible antecedents
  const pairedResponses = [];
  for (let i = 0; i < anaphors.length; ++i) {
    if (anaphors[i].includes('}')) {
      pairedResponses.push({
        anaphora: anaphors[i].substring(0, anaphors[i].indexOf('}')),
        antecedents: antecedents[i]
          .substring(0, antecedents[i].indexOf(']'))
          .split(', '),
      });
    }
  }

  // Clean up the item string by removing antecedents
  let cleanItem = item;
  for (let i = 0; i < pairedResponses.length; ++i) {
    cleanItem = cleanItem.replace(
      cleanItem.substring(
        cleanItem.indexOf(' ['),
        cleanItem.indexOf(']') + 1,
      ),
      '',
    );
  }

  // Split the words out of the paragraph
  const words = cleanItem.split(' ');

  // Iterate to find the important indices
  // and clean out any { }
  const anaphorsIndices = [];
  for (let i = 0; i < words.length; ++i) {
    if (words[i].includes('{')) {
      anaphorsIndices.push(i);
      words[i] = words[i].replace(/[{}]/g, '');
    }
  }

  const wordProps = [];
  // For iterating over the anaphorsIndices
  let indicesEntry = 0;

  // For creating a Set of antecendents,
  // which will be needed when draggables
  // are created
  let antecedentArr = [];
  const droppableProps = [];

  for (let i = 0; i < words.length; ++i) {
    // Anaphora needs to have a highlight
    if (i === anaphorsIndices[indicesEntry]) {
      antecedentArr = antecedentArr.concat(pairedResponses[indicesEntry].antecedents);
      // Create a droppableProp that will only accept its paired
      // antecedents
      droppableProps.push({
        id: `mystery-${i}-${words[i]}`,
        text: words[i],
        type: DROPPABLE_TYPE.ACTIVE,
        antecedents: pairedResponses[indicesEntry].antecedents,
      });
      wordProps[i] = {
        id: `droppable-${i}`,
        type: 'droppable',
      };
      indicesEntry++;
    } else {
      // If the word is not an anaphora,
      // make it plain
      wordProps[i] = {
        id: `mystery-${i}-${words[i]}`,
        text: words[i],
        type: WORD_TYPE.PLAIN,
      };
    }
  }

  // Make the antecedents into a Set, preventing
  // duplicates
  const antecedentSet = new Set(antecedentArr);
  const draggableWords = Array.from(antecedentSet);
  const draggableProps = [];

  // Iterate and create draggableProps
  for (let i = 0; i < draggableWords.length; ++i) {
    draggableProps.push({
      id: `${i}-${draggableWords[i]}`,
      shouldDisplay: true,
      text: draggableWords[i],
      // Should result in saved name in assets/icons/index.js
      icon: draggableWords[i]
        .replace(/(the )+|(a )+|(an )+|( )+|(')+/g, '')
        .toLowerCase(),
    });
  }

  return {
    anaphorsIndices,
    pairedResponses,
    words,
    wordProps,
    droppableProps,
    draggableProps,
  };
};
